import type { Edge, Node } from '@xyflow/react';

type UnknownRecord = Record<string, unknown>;

const CALLBACK_DATA_KEYS = new Set([
  'onAddAgentClick',
  'onAddClick',
  'onAddToolClick',
  'onClick',
  'onConfigure',
  'onDelete',
  'onDuplicate',
  'onEditWithAI',
  'onIterationChange',
  'onOpen',
  'onSubWorkflowExpandToggle',
  'onToggle',
]);

const CALLBACK_OWNER_DATA_KEYS = [
  'actionName',
  'actionType',
  'branchType',
  'childCount',
  'clusterKind',
  'interactionKind',
  'parentAgentId',
  'parentAgentName',
  'scopeLabel',
  'scopeParentId',
  'toolName',
  'toolNodeId',
] as const;

const CALLBACK_OWNER_ELEMENT_KEYS = [
  'id',
  'source',
  'sourceHandle',
  'target',
  'targetHandle',
  'type',
] as const;

const VALUE_ARRAY_DATA_KEYS = new Set(['cases', 'childNodeIds']);

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasSameOwnKeys = (left: UnknownRecord, right: UnknownRecord): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key))
  );
};

const shallowRecordEqual = (left: UnknownRecord, right: UnknownRecord): boolean =>
  hasSameOwnKeys(left, right) &&
  Object.keys(left).every((key) => Object.is(left[key], right[key]));

const reconcileShallowRecord = (
  previous: unknown,
  candidate: unknown,
): unknown => {
  if (Object.is(previous, candidate)) return previous;
  if (!isRecord(previous) || !isRecord(candidate)) return candidate;
  return shallowRecordEqual(previous, candidate) ? previous : candidate;
};

const areEquivalentValueArrays = (
  previous: unknown,
  candidate: unknown,
): boolean =>
  Array.isArray(previous) &&
  Array.isArray(candidate) &&
  previous.length === candidate.length &&
  previous.every(
    (value, index) =>
      (typeof value === 'string' || typeof value === 'number') &&
      Object.is(value, candidate[index]),
  );

const hasSameCallbackOwner = (
  previousElement: UnknownRecord,
  candidateElement: UnknownRecord,
  previousData: UnknownRecord,
  candidateData: UnknownRecord,
): boolean =>
  CALLBACK_OWNER_ELEMENT_KEYS.every((key) =>
    Object.is(previousElement[key], candidateElement[key]),
  ) &&
  CALLBACK_OWNER_DATA_KEYS.every((key) =>
    Object.is(previousData[key], candidateData[key]),
  );

const reconcileElementData = (
  previous: unknown,
  candidate: unknown,
  previousElement: UnknownRecord,
  candidateElement: UnknownRecord,
): unknown => {
  if (Object.is(previous, candidate)) return previous;
  if (!isRecord(previous) || !isRecord(candidate)) return candidate;

  const sameCallbackOwner = hasSameCallbackOwner(
    previousElement,
    candidateElement,
    previous,
    candidate,
  );
  let result: UnknownRecord | undefined;
  let matchesPrevious = hasSameOwnKeys(previous, candidate);

  for (const key of Object.keys(candidate)) {
    const previousValue = previous[key];
    const candidateValue = candidate[key];
    let reconciledValue = candidateValue;

    if (Object.is(previousValue, candidateValue)) {
      reconciledValue = previousValue;
    } else if (
      CALLBACK_DATA_KEYS.has(key) &&
      sameCallbackOwner &&
      typeof previousValue === 'function' &&
      typeof candidateValue === 'function'
    ) {
      reconciledValue = previousValue;
    } else if (
      VALUE_ARRAY_DATA_KEYS.has(key) &&
      areEquivalentValueArrays(previousValue, candidateValue)
    ) {
      reconciledValue = previousValue;
    }

    if (!Object.is(reconciledValue, candidateValue)) {
      result ??= { ...candidate };
      result[key] = reconciledValue;
    }
    if (!Object.is(reconciledValue, previousValue)) {
      matchesPrevious = false;
    }
  }

  return matchesPrevious ? previous : (result ?? candidate);
};

const reconcileElement = <T extends { id: string }>(
  previous: T | undefined,
  candidate: T,
  shallowNestedKeys: readonly string[],
): T => {
  if (!previous || previous === candidate) return candidate;

  const previousRecord = previous as unknown as UnknownRecord;
  const candidateRecord = candidate as unknown as UnknownRecord;
  let reconciledRecord: UnknownRecord | undefined;

  const replaceCandidateValue = (key: string, value: unknown): void => {
    if (Object.is(value, candidateRecord[key])) return;
    reconciledRecord ??= { ...candidateRecord };
    reconciledRecord[key] = value;
  };

  if (Object.prototype.hasOwnProperty.call(candidateRecord, 'data')) {
    replaceCandidateValue(
      'data',
      reconcileElementData(
        previousRecord.data,
        candidateRecord.data,
        previousRecord,
        candidateRecord,
      ),
    );
  }

  if (
    !Object.prototype.hasOwnProperty.call(candidateRecord, 'selected') &&
    Object.prototype.hasOwnProperty.call(previousRecord, 'selected')
  ) {
    replaceCandidateValue('selected', previousRecord.selected);
  }

  for (const key of shallowNestedKeys) {
    if (!Object.prototype.hasOwnProperty.call(candidateRecord, key)) continue;
    replaceCandidateValue(
      key,
      reconcileShallowRecord(previousRecord[key], candidateRecord[key]),
    );
  }

  const reconciledCandidate = reconciledRecord ?? candidateRecord;
  return shallowRecordEqual(previousRecord, reconciledCandidate)
    ? previous
    : (reconciledCandidate as unknown as T);
};

const reconcileElementsById = <T extends { id: string }>(
  previous: readonly T[],
  candidates: readonly T[],
  reconcile: (previous: T | undefined, candidate: T) => T,
): T[] => {
  if (previous === candidates) return previous as T[];

  const previousById = new Map(previous.map((element) => [element.id, element]));
  let result: T[] | undefined =
    previous.length === candidates.length ? undefined : [];

  candidates.forEach((candidate, index) => {
    const reconciled = reconcile(previousById.get(candidate.id), candidate);
    if (!result && reconciled !== previous[index]) {
      result = previous.slice(0, index);
    }
    result?.push(reconciled);
  });

  return result ?? (previous as T[]);
};

const NODE_SHALLOW_NESTED_KEYS = ['measured', 'position', 'style'] as const;
const EDGE_SHALLOW_NESTED_KEYS = [
  'domAttributes',
  'markerEnd',
  'markerStart',
  'style',
] as const;

export const reconcileWorkflowNodes = <T extends Node>(
  previous: readonly T[],
  candidates: readonly T[],
): T[] =>
  reconcileElementsById(previous, candidates, (previousNode, candidate) =>
    reconcileElement(previousNode, candidate, NODE_SHALLOW_NESTED_KEYS),
  );

export const reconcileWorkflowEdges = <T extends Edge>(
  previous: readonly T[],
  candidates: readonly T[],
): T[] =>
  reconcileElementsById(previous, candidates, (previousEdge, candidate) =>
    reconcileElement(previousEdge, candidate, EDGE_SHALLOW_NESTED_KEYS),
  );

export const updateNodeSelection = <T extends Node>(
  nodes: readonly T[],
  selectedNodeIds: ReadonlySet<string>,
): T[] => {
  let result: T[] | undefined;

  nodes.forEach((node, index) => {
    const shouldBeSelected = selectedNodeIds.has(node.id);
    if ((node.selected === true) === shouldBeSelected) return;

    result ??= [...nodes];
    result[index] = { ...node, selected: shouldBeSelected };
  });

  return result ?? (nodes as T[]);
};
